import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Aviso, Botao, CampoDeTexto, Modal, Selo, useDialogo } from '../componentes/ui';
import { requisicaoAutenticada } from '../servicos/http';
import { formatarCnpj, formatarCpf } from '../servicos/formato';
import { mascararTelefone } from '../servicos/documento';
import { ErroDaApi } from './ErroDaApi';

/** O que o lead simulou, somado. `null` = não simulou (≠ simulou e deu zero). */
export interface ResumoDaSimulacao {
  readonly meses: number;
  readonly creditoTotal: number;
  readonly beneficioTotal: number;
  readonly anos: readonly number[];
}

export interface PreCadastro {
  readonly id: string;
  readonly step: number;
  readonly aprovado: boolean;
  readonly rejeitado: boolean;
  readonly personFullName: string | null;
  readonly personEmailAddress: string | null;
  readonly personTaxpayerId: string | null;
  readonly personPhone: string | null;
  /** Vínculo com a empresa calculada, informado na calculadora (Sócio, Decisor…). */
  readonly personOccupation?: string | null;
  /** Já decidido pelo servidor: quem marcou "gerencio uma carteira de empresas". */
  readonly perfilAssessor?: boolean;
  readonly legalName: string | null;
  readonly taxpayerId: string | null;
  readonly emailAddress: string | null;
  readonly phone: string | null;
  readonly addressStreetName: string | null;
  readonly addressStreetNumber: string | null;
  readonly addressDistrict: string | null;
  readonly addressCity: string | null;
  readonly addressStateCode: string | null;
  readonly addressPostalCode: string | null;
  readonly temSenhaDefinida?: boolean;
  readonly audCreatedAt?: string;
  readonly simulacao?: ResumoDaSimulacao | null;
}

interface ResultadoAprovacao {
  readonly status: 'APROVADO';
  readonly perfil: 'CLIENTE' | 'ASSESSOR';
  readonly businessId: string;
  readonly accountId: string;
  readonly empresaReaproveitada: boolean;
  readonly contaReaproveitada: boolean;
  readonly simulacoesMigradas: number;
  readonly senhaTemporaria: string | null;
  readonly avisos: readonly string[];
}

interface DocumentoDoRegistro {
  readonly tipo: string;
  readonly nome: string;
  readonly tamanho: number;
}

/**
 * RN-330 — Analisar um pré-cadastro: aprovar ou rejeitar.
 *
 * ## O que faltava
 *
 * `POST /register/:id/approve` e `/reject` existiam desde o começo. A fila "Em
 * Aprovação" era **somente leitura**: dava para ver quem estava esperando e não
 * para decidir. Aprovar provisiona empresa, pessoa, conta e vínculo — e isso só
 * acontecia por chamada direta à API.
 *
 * ## A senha temporária aparece uma vez
 *
 * Quando o pré-cadastro não trouxe senha, o servidor gera uma e a devolve **só
 * nesta resposta** — ela não fica guardada em claro em lugar nenhum. Por isso a
 * tela a destaca e avisa que não dá para consultá-la depois: quem fechar o
 * modal sem copiar precisa mandar o titular usar "esqueci minha senha".
 *
 * ## Empresa que já existe não é erro
 *
 * O CNPJ pode já estar cadastrado — um assessor cadastrou o cliente antes, com
 * SPED e histórico. Aprovar nesse caso **vincula** em vez de duplicar, mas exige
 * confirmação explícita: o cadastro existente não é sobrescrito pelo que veio de
 * um formulário público (dívida técnica #28).
 */
export function AnaliseDoPreCadastro(props: {
  readonly registro: PreCadastro | null;
  readonly aoFechar: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const { confirmar } = useDialogo();
  const [motivo, setMotivo] = useState('');
  const [confirmarEmpresa, setConfirmarEmpresa] = useState(false);

  const id = props.registro?.id ?? '';

  const documentos = useQuery({
    queryKey: ['documentos-registro', id],
    queryFn: () => requisicaoAutenticada<DocumentoDoRegistro[]>(`/register/${id}/documentos`),
    enabled: props.registro !== null,
  });

  const invalidar = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['pre-cadastros'] });
    void queryClient.invalidateQueries({ queryKey: ['notificacoes'] });
  };

  const aprovar = useMutation({
    mutationFn: () =>
      requisicaoAutenticada<ResultadoAprovacao>(`/register/${id}/approve`, {
        metodo: 'POST',
        corpo: { confirmarEmpresaExistente: confirmarEmpresa },
      }),
    onSuccess: invalidar,
  });

  const rejeitar = useMutation({
    mutationFn: () =>
      requisicaoAutenticada<unknown>(`/register/${id}/reject`, {
        metodo: 'POST',
        corpo: motivo.trim() === '' ? {} : { motivo: motivo.trim() },
      }),
    onSuccess: () => {
      invalidar();
      fechar();
    },
  });

  const fechar = (): void => {
    aprovar.reset();
    rejeitar.reset();
    setMotivo('');
    setConfirmarEmpresa(false);
    props.aoFechar();
  };

  const r = props.registro;

  // O 409 de empresa existente é o único que se resolve na própria tela: a saída
  // é confirmar, não corrigir o cadastro.
  const pedeConfirmacao =
    aprovar.isError && /já existe|já tem usuários|confirme/i.test(aprovar.error.message);

  /**
   * A resposta da API (recusa ou pedido de confirmação) fica no **topo** do
   * corpo e é rolada para a vista: o corpo do modal rola (60vh), e com o
   * aviso no fim a pessoa clicava em "Aprovar", via a tela piscar e nada mais —
   * o 409 "empresa já cadastrada, confirme" estava lá embaixo, fora da vista.
   */
  const avisoDaResposta = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (aprovar.isError || rejeitar.isError) {
      avisoDaResposta.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [aprovar.isError, rejeitar.isError]);

  return (
    <Modal
      aberto={r !== null}
      aoFechar={fechar}
      titulo="Analisar pré-cadastro"
      descricao={r?.legalName ?? r?.personFullName ?? undefined}
      largura="larga"
      rodape={
        aprovar.isSuccess ? (
          <Botao onClick={fechar}>Concluir</Botao>
        ) : (
          <>
            <Botao variante="secundario" onClick={fechar}>
              Fechar
            </Botao>
            <Botao
              variante="perigo"
              carregando={rejeitar.isPending}
              onClick={async () => {
                // O motivo já foi digitado no campo da tela; aqui só se confirma a decisão.
                const ok = await confirmar({
                  titulo: 'Rejeitar este pré-cadastro?',
                  descricao:
                    motivo.trim() === ''
                      ? 'A decisão fica registrada, sem motivo informado.'
                      : 'A decisão fica registrada junto com o motivo informado.',
                  rotuloConfirmar: 'Rejeitar',
                  tom: 'perigo',
                });
                if (ok) rejeitar.mutate();
              }}
            >
              Rejeitar
            </Botao>
            <Botao carregando={aprovar.isPending} onClick={() => aprovar.mutate()}>
              {pedeConfirmacao && confirmarEmpresa ? 'Aprovar mesmo assim' : 'Aprovar'}
            </Botao>
          </>
        )
      }
    >
      {aprovar.isSuccess ? (
        <ResultadoDaAprovacao resultado={aprovar.data} />
      ) : (
        <div className="flex flex-col gap-5">
          <div ref={avisoDaResposta} className="flex flex-col gap-5 empty:hidden">
            {pedeConfirmacao && (
              <Aviso tom="atencao" titulo="Esta empresa já está cadastrada">
                <p>{aprovar.error.message}</p>
                <label className="mt-2 flex cursor-pointer items-start gap-2">
                  <input
                    type="checkbox"
                    checked={confirmarEmpresa}
                    onChange={(e) => setConfirmarEmpresa(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-borda"
                  />
                  <span className="text-sm">
                    Confirmo o vínculo à empresa existente. O cadastro dela <strong>não</strong> é
                    sobrescrito pelos dados do formulário. Depois de marcar, clique em{' '}
                    <strong>Aprovar mesmo assim</strong>.
                  </span>
                </label>
              </Aviso>
            )}
            {aprovar.isError && !pedeConfirmacao && (
              <ErroDaApi titulo="Não foi possível aprovar" erro={aprovar.error} />
            )}
            {rejeitar.isError && (
              <ErroDaApi titulo="Não foi possível rejeitar" erro={rejeitar.error} />
            )}
          </div>

          {r !== null && <DadosDoRegistro registro={r} />}

          {r?.simulacao != null && (
            <Aviso tom="informacao" titulo="Este contato veio da calculadora">
              Simulou {r.simulacao.meses} {r.simulacao.meses === 1 ? 'mês' : 'meses'} (
              {r.simulacao.anos.join(', ')}):{' '}
              {r.simulacao.creditoTotal.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              })}{' '}
              de crédito, com benefício de{' '}
              {r.simulacao.beneficioTotal.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              })}
              . Aprovar migra essas simulações para o cliente.
            </Aviso>
          )}

          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold text-conteudo-primario">
              Documentos anexados ({documentos.data?.length ?? 0})
            </h3>
            {(documentos.data ?? []).length === 0 ? (
              <p className="text-sm text-conteudo-sutil">
                Nenhum documento. A análise pode seguir — os documentos podem ser pedidos depois,
                pela tela da empresa.
              </p>
            ) : (
              <ul className="flex flex-col gap-1">
                {(documentos.data ?? []).map((d) => (
                  <li key={d.nome} className="text-sm text-conteudo-primario">
                    {d.nome} <span className="text-xs text-conteudo-sutil">({d.tipo})</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <Aviso tom="informacao" titulo="O que a aprovação faz">
            {r?.perfilAssessor === true ? (
              <>
                Cria (ou reaproveita) a <strong>empresa de assessoria</strong> como Parceiro ICMS,
                cria a pessoa e a conta de acesso, e vincula o titular como <strong>Gestor</strong>{' '}
                dela. Se o cadastro trouxe uma empresa cliente, ela também é criada e as simulações
                do funil migram para ela.
              </>
            ) : (
              <>
                Cria (ou reaproveita) a empresa, cria a pessoa e a conta de acesso, e vincula o
                titular como <strong>Gestor</strong>. As simulações que ele deixou no funil migram
                para o cliente.
              </>
            )}
          </Aviso>

          <CampoDeTexto
            rotulo="Motivo da rejeição"
            rows={2}
            ajuda="Só usado ao rejeitar. Fica gravado — é o que o próximo admin lê para não reanalisar o mesmo caso."
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
          />
        </div>
      )}
    </Modal>
  );
}

/* ─── Blocos ──────────────────────────────────────────────────────────────── */

function DadosDoRegistro(props: { readonly registro: PreCadastro }): JSX.Element {
  const r = props.registro;
  const endereco = [
    [r.addressStreetName, r.addressStreetNumber].filter(Boolean).join(', '),
    r.addressDistrict,
    [r.addressCity, r.addressStateCode].filter(Boolean).join('/'),
    r.addressPostalCode,
  ]
    .filter((p) => p !== null && p !== '')
    .join(' — ');

  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
      {/*
        O perfil vem antes de tudo: é o que muda o que "Aprovar" faz. Assessor
        vira Parceiro ICMS e a empresa dele é criada; contribuinte vira cliente.
      */}
      <Linha rotulo="Perfil">
        <Selo tom={r.perfilAssessor === true ? 'progresso' : 'neutro'}>
          {r.perfilAssessor === true ? 'Assessor' : 'Contribuinte'}
        </Selo>
      </Linha>
      <Linha rotulo="Vínculo com a empresa">{r.personOccupation ?? '—'}</Linha>
      <Linha rotulo="Responsável">{r.personFullName ?? '—'}</Linha>
      <Linha rotulo="E-mail do responsável">
        <span className="valor-fiscal text-sm">{r.personEmailAddress ?? '—'}</span>
      </Linha>
      <Linha rotulo="CPF">
        {r.personTaxpayerId === null ? '—' : formatarCpf(r.personTaxpayerId)}
      </Linha>
      <Linha rotulo="Telefone">{r.personPhone ? mascararTelefone(r.personPhone) : '—'}</Linha>
      <Linha rotulo="Empresa">{r.legalName ?? '—'}</Linha>
      <Linha rotulo="CNPJ">{r.taxpayerId === null ? '—' : formatarCnpj(r.taxpayerId)}</Linha>
      {endereco !== '' && <Linha rotulo="Endereço">{endereco}</Linha>}
      <Linha rotulo="Senha definida no wizard">
        <Selo tom={r.temSenhaDefinida === true ? 'positivo' : 'neutro'}>
          {r.temSenhaDefinida === true ? 'Sim' : 'Não — será gerada'}
        </Selo>
      </Linha>
    </dl>
  );
}

function ResultadoDaAprovacao(props: { readonly resultado: ResultadoAprovacao }): JSX.Element {
  const r = props.resultado;

  return (
    <div className="flex flex-col gap-4">
      <Aviso
        tom="positivo"
        titulo={`Aprovado como ${r.perfil === 'CLIENTE' ? 'cliente' : 'assessor'}`}
      >
        Empresa {r.empresaReaproveitada ? 'vinculada (já existia)' : 'criada'} e conta{' '}
        {r.contaReaproveitada ? 'reaproveitada' : 'criada'}.
        {r.simulacoesMigradas > 0 && ` ${r.simulacoesMigradas} simulação(ões) migrada(s).`}
      </Aviso>

      {r.senhaTemporaria !== null && (
        <Aviso tom="atencao" titulo="Senha temporária — anote agora">
          <p className="valor-fiscal mt-1 select-all rounded border border-borda bg-superficie-base px-3 py-2 text-base">
            {r.senhaTemporaria}
          </p>
          <p className="mt-2">
            Ela não fica guardada em claro e <strong>não dá para consultá-la depois</strong>.
            Entregue ao titular; se perder, o caminho é “esqueci minha senha”.
          </p>
        </Aviso>
      )}

      {r.avisos.length > 0 && (
        <Aviso tom="informacao" titulo="Avisos da aprovação">
          <ul className="mt-1 flex list-disc flex-col gap-1 pl-4">
            {r.avisos.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </Aviso>
      )}

      <Link
        to={`/empresas/${r.businessId}`}
        className="text-sm text-acento-ciano underline-offset-4 hover:underline"
      >
        Abrir o cadastro da empresa →
      </Link>
    </div>
  );
}

function Linha(props: {
  readonly rotulo: string;
  readonly children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs uppercase tracking-wide text-conteudo-sutil">{props.rotulo}</dt>
      <dd className="text-sm text-conteudo-primario">{props.children}</dd>
    </div>
  );
}
